import React from 'react';
import { Pin, Trash2, MoreHorizontal, Pencil } from 'lucide-react';
import { cn } from '../../../utils/cn';

interface ConversationListItemProps {
  id: string;
  title: string | null;
  lastMessageAt: string | null;
  isPinned: boolean;
  isActive: boolean;
  onClick: () => void;
  onPin: () => void;
  onDelete: () => void;
  onRename: (newTitle: string) => void;
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const ConversationListItem: React.FC<ConversationListItemProps> = ({
  id,
  title,
  lastMessageAt,
  isPinned,
  isActive,
  onClick,
  onPin,
  onDelete,
  onRename,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(title || '');
  const menuRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setEditValue(title || '');
  }, [title]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayTitle = title || 'New conversation';

  const handleSaveRename = () => {
    if (!isEditing) return;
    const finalValue = editValue.trim();
    if (finalValue && finalValue !== title) {
      onRename(finalValue);
    } else {
      setEditValue(title || ''); // revert if empty
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveRename();
    } else if (e.key === 'Escape') {
      setEditValue(title || '');
      setIsEditing(false);
    }
  };

  return (
    <div
      className={cn(
        'group relative flex items-center gap-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200',
        isActive
          ? 'bg-indigo-600/15 border border-indigo-500/30 text-white'
          : 'hover:bg-white/5 text-gray-400 hover:text-white border border-transparent'
      )}
      onClick={onClick}
      data-test-id={`conversation-item-${id}`}
    >
      {/* Pin indicator */}
      {isPinned && (
        <Pin className="h-3 w-3 text-indigo-400 shrink-0 -rotate-45" />
      )}

      {/* Title + Time */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSaveRename}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="w-full text-sm font-medium bg-gray-800/50 text-white border border-indigo-500/50 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-500"
          />
        ) : (
          <p
            className="text-sm font-medium truncate cursor-text select-none"
            title={displayTitle}
            onDoubleClick={e => {
              e.stopPropagation();
              setIsEditing(true);
            }}
            data-test-id={`conversation-title-${id}`}
          >
            {displayTitle}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-0.5">
          {formatRelativeTime(lastMessageAt)}
        </p>
      </div>

      {/* Context menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={e => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className={cn(
            'p-1 rounded-md transition-all',
            showMenu
              ? 'bg-white/10 text-white'
              : 'opacity-0 group-hover:opacity-100 hover:bg-white/10 text-gray-500 hover:text-white'
          )}
          data-test-id={`conversation-menu-${id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>

        {showMenu && (
          <div className="absolute right-0 top-full mt-1 w-40 bg-[#1c1c26] border border-white/10 rounded-xl shadow-2xl z-50 py-1 animate-in fade-in duration-150">
            <button
              onClick={e => {
                e.stopPropagation();
                setIsEditing(true);
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
              data-test-id={`conversation-rename-${id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
              Rename
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                onPin();
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
              data-test-id={`conversation-pin-${id}`}
            >
              <Pin className="h-3.5 w-3.5" />
              {isPinned ? 'Unpin' : 'Pin'}
            </button>
            <button
              onClick={e => {
                e.stopPropagation();
                onDelete();
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
              data-test-id={`conversation-delete-${id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
